/**
 * <LiveAppBanner> — « Installer l'appli », pendant le live, dans un navigateur de téléphone (molecule).
 *
 * Le bandeau de l'ancien live (`LiveWebMobileBanner`), ses mots et ses gestes : dans un navigateur
 * de téléphone, le micro, la photo et le retour d'arrière-plan lâchent ; l'app est plus stable.
 * Deux formes, en props :
 *  - **ouvert** : le téléphone, la phrase, « Installer » (la fiche de l'app), et la croix qui le
 *    replie ;
 *  - **replié** : une fine ligne pleine largeur, « L'APPLI MASTEUR », qui le rouvre au toucher. Il
 *    reste là tout le live.
 *
 * Une barre pleine largeur sous l'en-tête, aux tons de l'alerte, comme les autres barres du live
 * (l'attente du masteur, la connexion faible). L'écran décide quand il se montre (le web d'un
 * téléphone) et mesure les gestes.
 *
 * Contrôlée : `collapsed` arrive, les gestes remontent.
 */
import React from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Button } from "../../atoms/Buttons/Button"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { Row } from "../../atoms/containers/Row"
import { Icon } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"

export type LiveAppBannerProps = {
    /** Replié : la fine ligne. Défaut `false`. */
    collapsed?: boolean
    /** « Installer » : la fiche de l'app. */
    onInstall: () => void
    /** La croix : le bandeau se replie. */
    onCollapse: () => void
    /** La ligne repliée touchée : il se rouvre. */
    onExpand: () => void
    testID?: string
}

const TEXTS = {
    message: "Avec l'appli Masteur, ton live est bien plus stable.",
    install: "Installer",
    collapse: "Replier le bandeau",
    collapsed: "L'APPLI MASTEUR",
    expand: "L'appli Masteur, afficher le bandeau",
}

/** L'encre de l'alerte, sur sa tinte : celle de la connexion faible. */
const INK = "warning-strong"
/** La cible d'un doigt, autour d'une croix plus petite. */
const CLOSE_SIZE = 44

export function LiveAppBanner(props: LiveAppBannerProps) {
    const { collapsed = false, onInstall, onCollapse, onExpand, testID } = props

    if (collapsed) {
        return (
            <Pressable
                onPress={onExpand}
                accessibilityRole="button"
                accessibilityLabel={TEXTS.expand}
                style={styles.thin}
                testID={testID}
            >
                <Row gap="2" align="center" justify="center">
                    <Icon name="mobile-alt" size={12} color={INK} />
                    <Text size="text-xs" weight="semibold" color={INK}>
                        {TEXTS.collapsed}
                    </Text>
                </Row>
            </Pressable>
        )
    }

    return (
        <View style={styles.bar} testID={testID}>
            <Row gap="2" align="center">
                <Icon name="mobile-alt" size={16} color={INK} />
                <Text size="text-sm" weight="semibold" color={INK} style={styles.message}>
                    {TEXTS.message}
                </Text>
                <Button label={TEXTS.install} size="sm" color="warning" onPress={onInstall} />
                <IconButton
                    iconName="times"
                    appearance="transparent"
                    iconColor={INK}
                    size={CLOSE_SIZE}
                    iconSize={16}
                    onPress={onCollapse}
                    accessibilityLabel={TEXTS.collapse}
                />
            </Row>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    bar: {
        backgroundColor: theme.colors.warningLight,
        paddingLeft: theme.spacing["4"],
        paddingRight: theme.spacing["1"],
        paddingVertical: theme.spacing["1"],
    },
    message: { flex: 1, flexShrink: 1 },
    thin: {
        backgroundColor: theme.colors.warningLight,
        paddingVertical: theme.spacing["1"],
    },
}))
